import { gql } from "@urql/core";
import { Client, CombinedError, cacheExchange, fetchExchange } from "urql";

// Helper to check if error is an AbortError
const isAbortError = (error: unknown): boolean => {
    // Check direct Error instances
    if (
        error instanceof Error &&
        (error.name === "AbortError" || error.message?.includes("aborted"))
    ) {
        return true;
    }

    // Check for urql CombinedError with wrapped networkError
    if (
        error &&
        typeof error === "object" &&
        "networkError" in error &&
        error.networkError instanceof Error
    ) {
        const networkError = error.networkError;
        return (
            networkError.name === "AbortError" ||
            networkError.message?.includes("aborted") ||
            networkError.message?.includes("The user aborted a request")
        );
    }

    return false;
};

import { GATEWAY_URL } from "./constants";
import { AccountUserProfileFragment } from "./fragments/account-user-profile-fragment";
import { OnboardingContextFragment } from "./fragments/onboarding-context-fragment";
import { PaidChannelContextFragment } from "./fragments/paid-channel-context-fragment";
import { RecommendationsFragment } from "./fragments/recommendations-fragment";
import { SitesFragment } from "./fragments/sites-fragment";
import { UserFootprintsFragment } from "./fragments/user-footprints-fragment";
import { UserProfileFragment } from "./fragments/user-profile-fragment";
import { UserProfileJobFunctionFragment } from "./fragments/user-profile-job-function-fragment";
import {
    AccountProfileOptionalFields,
    GetGrowthUnifiedProfileArgs,
    GetGrowthUnifiedProfileResponse,
    GetGrowthUnifiedProfileAccountProfileArgs,
    GrowthUnifiedProfileClientOptions,
    GrowthUnifiedProfilePFUFilter,
    OptionalFields,
    UpdateGrowthUnifiedProfileResponse
} from "./types";
import { CreateUnifiedProfileInput } from "./unified-profile.schema";

export class GrowthUnifiedProfileClient {
    private readonly client: Client;
    private readonly fetchOptions;

    public constructor(options?: GrowthUnifiedProfileClientOptions) {
        this.fetchOptions = options?.slauthToken
            ? {
                  headers: {
                      Authorization: `SLAUTH ${options.slauthToken || ""}`
                  }
              }
            : {
                  credentials: "include" as RequestCredentials
              };
        this.client = new Client({
            url: GATEWAY_URL,
            exchanges: [cacheExchange, fetchExchange],
            fetchOptions: this.fetchOptions
        });
    }
    private constructProfileQuery(optionalFields?: OptionalFields[]) {
        const fragments = [
            optionalFields?.includes(OptionalFields.ONBOARDING_CONTEXT)
                ? "...OnboardingContextFragment"
                : "",
            optionalFields?.includes(OptionalFields.SITES)
                ? "...SitesFragment"
                : "",
            optionalFields?.includes(OptionalFields.PAID_CHANNEL_CONTEXT)
                ? "...PaidChannelContextFragment"
                : "",
            optionalFields?.includes(OptionalFields.USER_FOOTPRINTS)
                ? "...UserFootprintsFragment"
                : "",
            optionalFields?.includes(OptionalFields.USER_PROFILE)
                ? "...UserProfileFragment"
                : "",
            optionalFields?.includes(OptionalFields.USER_PROFILE_JOB_FUNCTION)
                ? "...UserProfileJobFunctionFragment"
                : ""
        ]
            .filter(Boolean)
            .join("\n");

        // If no optional fields are provided, default to onboarding context
        return fragments || "...OnboardingContextFragment";
    }

    private constructAccountProfileQuery(
        optionalFields?: AccountProfileOptionalFields[]
    ) {
        const fragments = [
            this.hasAccountProfileField(
                optionalFields,
                AccountProfileOptionalFields.USER_PROFILE
            )
                ? "...AccountUserProfileFragment"
                : "",
            this.hasAccountProfileField(
                optionalFields,
                AccountProfileOptionalFields.PAID_FEATURE_USAGE
            )
                ? `paidFeatureUsage(filter: $pfuFilter) {
                    featureType
                    featureName
                    featureSupportedEdition
                    usageSummary {
                        tenants {
                            tenantId
                            tenantType
                        }
                        products
                        entitlementIds
                        featureEngagementLevel
                        usageCount
                    }
                }`
                : "",
            this.hasAccountProfileField(
                optionalFields,
                AccountProfileOptionalFields.RECOMMENDATIONS
            )
                ? "...RecommendationsFragment"
                : ""
        ]
            .filter(Boolean)
            .join("\n");

        return fragments;
    }
    /**
     * Get a unified profile
     * @param accountId
     * @returns { data, error }
     */
    // eslint-disable-next-line complexity
    public getProfile = async (
        args: GetGrowthUnifiedProfileArgs,
        optionalFields?: OptionalFields[],
        optionalHeaderUseCase?: string,
        abortSignal?: AbortSignal
    ) => {
        const { accountId, anonymousId, tenantId } = args;
        const fragments = this.constructProfileQuery(optionalFields);

        const GET_PROFILE_QUERY = gql`
            query getUnifiedProfile($accountId: ID, $tenantId: ID, $anonymousId: ID) {
                growthUnifiedProfile_getUnifiedProfile(
                    accountId: $accountId
                    tenantId: $tenantId
                    anonymousId: $anonymousId
                ) {
                    ${fragments}
                }
            }
            ${
                optionalFields?.includes(OptionalFields.ONBOARDING_CONTEXT)
                    ? OnboardingContextFragment
                    : ""
            }
            ${
                optionalFields?.includes(OptionalFields.SITES)
                    ? SitesFragment
                    : ""
            }
            ${
                optionalFields?.includes(OptionalFields.PAID_CHANNEL_CONTEXT)
                    ? PaidChannelContextFragment
                    : ""
            }
            ${
                optionalFields?.includes(OptionalFields.USER_FOOTPRINTS)
                    ? UserFootprintsFragment
                    : ""
            }
            ${
                optionalFields?.includes(OptionalFields.USER_PROFILE)
                    ? UserProfileFragment
                    : ""
            }
            ${
                optionalFields?.includes(
                    OptionalFields.USER_PROFILE_JOB_FUNCTION
                )
                    ? UserProfileJobFunctionFragment
                    : ""
            }
        `;
        const fetchContext = this.buildFetchContext(
            optionalHeaderUseCase,
            abortSignal
        );

        try {
            const {
                data,
                error
            }: {
                data?: GetGrowthUnifiedProfileResponse;
                error?: CombinedError | undefined;
            } = await this.client.query(
                GET_PROFILE_QUERY,
                {
                    accountId,
                    tenantId,
                    anonymousId
                },
                {
                    fetchOptions: fetchContext
                }
            );

            return {
                data: data?.growthUnifiedProfile_getUnifiedProfile,
                error
            };
        } catch (error) {
            // Re-throw AbortErrors as they're expected when requests are cancelled
            if (isAbortError(error)) {
                throw error;
            }
            return { data: undefined, error };
        }
    };

    /**
     * Create or update a unified profile
     * @param profile
     * @returns { data, error }
     */
    public updateProfile = async ({
        profile,
        abortSignal
    }: {
        profile: Partial<CreateUnifiedProfileInput>;
        abortSignal?: AbortSignal;
    }) => {
        // Currently accountId is required, once anonymousId support
        // is added to the upstream service, this check can be removed
        if (!profile.accountId) {
            throw new Error("accountId is required");
        }

        const UPDATE_PROFILE_MUTATION = gql`
            mutation createUnifiedProfile(
                $profile: GrowthUnifiedProfileCreateProfileInput!
            ) {
                growthUnifiedProfile_createUnifiedProfile(profile: $profile) {
                    ...OnboardingContextFragment
                }
            }
            ${OnboardingContextFragment}
        `;

        try {
            const {
                data,
                error
            }: {
                data?: UpdateGrowthUnifiedProfileResponse;
                error?: CombinedError | undefined;
            } = await this.client.mutation(
                UPDATE_PROFILE_MUTATION,
                {
                    profile
                },
                {
                    fetchOptions: abortSignal
                        ? { signal: abortSignal }
                        : undefined
                }
            );

            return {
                data: data?.growthUnifiedProfile_createUnifiedProfile,
                error
            };
        } catch (error) {
            // Re-throw AbortErrors as they're expected when requests are cancelled
            if (isAbortError(error)) {
                throw error;
            }
            return { data: undefined, error };
        }
    };

    private getDefaultPFUFilter(): GrowthUnifiedProfilePFUFilter {
        const defaultStartDate = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000)
            .toISOString()
            .split("T")[0] as string; // 90 days ago in YYYY-MM-DD format
        return {
            startDate: defaultStartDate
        };
    }

    private resolvePFUFilter(
        optionalFields?: AccountProfileOptionalFields[],
        pfuFilter?: GrowthUnifiedProfilePFUFilter
    ): GrowthUnifiedProfilePFUFilter | null {
        const needsPFUFilter =
            optionalFields?.includes(
                AccountProfileOptionalFields.PAID_FEATURE_USAGE
            ) && !pfuFilter;

        if (needsPFUFilter) {
            return this.getDefaultPFUFilter();
        }
        return pfuFilter || null;
    }

    private hasAccountProfileField(
        optionalFields: AccountProfileOptionalFields[] | undefined,
        field: AccountProfileOptionalFields
    ): boolean {
        return optionalFields?.includes(field) ?? false;
    }

    private buildFetchContext(
        optionalHeaderUseCase?: string,
        abortSignal?: AbortSignal
    ) {
        return {
            ...this.fetchOptions,
            headers: {
                ...(this.fetchOptions?.headers || {}),
                ...(optionalHeaderUseCase
                    ? { "x-use-case": optionalHeaderUseCase }
                    : {})
            },
            ...(abortSignal ? { signal: abortSignal } : {})
        };
    }

    private buildGetAccountProfileQuery(
        optionalFields: AccountProfileOptionalFields[] | undefined,
        fragments: string,
        includePfuFilter: boolean
    ) {
        return gql`
            query getAccountProfile(
                $accountId: ID!
                ${
                    includePfuFilter
                        ? "$pfuFilter: GrowthUnifiedProfilePFUFilter"
                        : ""
                }
            ) {
                growthUnifiedProfile_getAccountProfile(accountId: $accountId) {
                    ${fragments}
                }
            }
            ${
                this.hasAccountProfileField(
                    optionalFields,
                    AccountProfileOptionalFields.USER_PROFILE
                )
                    ? AccountUserProfileFragment
                    : ""
            }
            ${
                this.hasAccountProfileField(
                    optionalFields,
                    AccountProfileOptionalFields.RECOMMENDATIONS
                )
                    ? RecommendationsFragment
                    : ""
            }
        `;
    }

    /**
     * Get an account profile
     * @param args - Account profile arguments including accountId and optional PFU filter
     * @param optionalFields - Optional fields to query (USER_PROFILE, PAID_FEATURE_USAGE, RECOMMENDATIONS)
     * @param optionalHeaderUseCase - Optional header use case
     * @param abortSignal - Optional AbortSignal to cancel the request
     * @returns { data, error }
     * @note If PAID_FEATURE_USAGE is requested but pfuFilter is not provided, a default filter (90 days) will be used.
     *       The GraphQL schema requires a filter for paidFeatureUsage queries.
     */
    public getAccountProfile = async (
        args: GetGrowthUnifiedProfileAccountProfileArgs,
        optionalFields?: AccountProfileOptionalFields[],
        optionalHeaderUseCase?: string,
        abortSignal?: AbortSignal
    ) => {
        const { accountId, pfuFilter } = args;
        const fragments = this.constructAccountProfileQuery(optionalFields);

        if (!fragments) {
            return {
                data: undefined,
                error: new Error(
                    "At least one optional field must be specified: USER_PROFILE, PAID_FEATURE_USAGE, or RECOMMENDATIONS"
                )
            };
        }

        const includePfuFilter = this.hasAccountProfileField(
            optionalFields,
            AccountProfileOptionalFields.PAID_FEATURE_USAGE
        );
        const filterToUse = this.resolvePFUFilter(optionalFields, pfuFilter);
        const variables = includePfuFilter
            ? { accountId, pfuFilter: filterToUse }
            : { accountId };

        try {
            const { data, error } = await this.client.query(
                this.buildGetAccountProfileQuery(
                    optionalFields,
                    fragments,
                    includePfuFilter
                ),
                variables,
                {
                    fetchOptions: this.buildFetchContext(
                        optionalHeaderUseCase,
                        abortSignal
                    )
                }
            );

            return {
                data: data?.growthUnifiedProfile_getAccountProfile,
                error
            };
        } catch (error) {
            if (isAbortError(error)) {
                throw error;
            }
            return { data: undefined, error };
        }
    };
}
